import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  X,
  FileText,
  Building2,
  Calendar,
  Newspaper,
  Archive
} from 'lucide-react';
import { getDetailedProcessingStatus, ProcessingStatusWithMetadata } from '../lib/api/index.js';
import { useWebSocket } from '../hooks/useWebSocket.js';

interface UploadProgressCardProps {
  announcementId: string;
  onComplete?: (status: ProcessingStatusWithMetadata) => void;
  onClose?: () => void;
  onError?: (error: string) => void;
  onStatusChange?: (status: string) => void; // Callback to update parent's job status
}

export function UploadProgressCard({
  announcementId,
  onComplete,
  onClose,
  onError,
  onStatusChange
}: UploadProgressCardProps) {
  const [status, setStatus] = useState<ProcessingStatusWithMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [articleTab, setArticleTab] = useState<'new' | 'legacy'>('new');

  // Stable refs for callbacks to prevent useEffect re-running when parent re-renders
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  // WebSocket for real-time updates
  // Channel must match exactly what the backend emits via event_emitter.py:
  // emit_status_change/emit_article_created/emit_processing_error emit to:
  //   "assayerai:announcement:{announcement_id}"  (announcement-specific)
  //   "assayerai:announcements"                   (general broadcast)
  // V2 has no tenant isolation — all articles are system-level (customer_id = NULL).
  const wsChannels = [`assayerai:announcement:${announcementId}`];

  useWebSocket({
    channels: wsChannels,
    onEvent: {
      status_change: async () => {
        // Fetch updated status when we receive a status change event
        try {
          const data = await getDetailedProcessingStatus(announcementId);
          setStatus(data);

          // Notify parent of status change
          if (onStatusChangeRef.current) {
            onStatusChangeRef.current(data.status);
          }

          // Trigger callbacks
          if (data.status === 'completed' && onCompleteRef.current) {
            onCompleteRef.current(data);
          }
          if (data.status === 'failed' && onErrorRef.current && data.error_message) {
            onErrorRef.current(data.error_message);
          }
        } catch (err) {
          console.error('Failed to fetch status after WebSocket event:', err);
        }
      },
      article_created: async () => {
        // Refresh when article is created
        try {
          const data = await getDetailedProcessingStatus(announcementId);
          setStatus(data);
        } catch (err) {
          console.error('Failed to fetch status after article created:', err);
        }
      },
      processing_error: async () => {
        // Refresh on error
        try {
          const data = await getDetailedProcessingStatus(announcementId);
          setStatus(data);
          if (data.error_message && onErrorRef.current) {
            onErrorRef.current(data.error_message);
          }
        } catch (err) {
          console.error('Failed to fetch status after error:', err);
        }
      },
    },
    autoInvalidate: false, // We handle updates manually
    debug: false,
  });

  // Initial fetch — only re-runs when announcementId changes.
  // Callbacks are read via refs to avoid re-triggering the effect on parent re-renders.
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await getDetailedProcessingStatus(announcementId);
        setStatus(data);

        // Notify parent of initial status
        if (onStatusChangeRef.current) {
          onStatusChangeRef.current(data.status);
        }

        // Trigger callbacks for initial state
        if (data.status === 'completed' && onCompleteRef.current) {
          onCompleteRef.current(data);
        }
        if (data.status === 'failed' && onErrorRef.current && data.error_message) {
          onErrorRef.current(data.error_message);
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to fetch status';
        setError(errorMsg);
        if (onErrorRef.current) {
          onErrorRef.current(errorMsg);
        }
      }
    };

    fetchStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcementId]); // Callbacks accessed via refs — stable, won't cause re-fetch loops

  const getStatusIcon = () => {
    if (!status) return <Loader2 className="w-5 h-5 text-grey-400 animate-spin" />;

    switch (status.status) {
      case 'uploaded':
      case 'queued':
        return <Loader2 className="w-5 h-5 text-grey-400 animate-spin" />;
      case 'extracting':
      case 'enriching':
      case 'generating':
        return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'completed':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    if (!status) return 'text-grey-600';

    switch (status.status) {
      case 'uploaded':
      case 'queued':
        return 'text-grey-600';
      case 'extracting':
      case 'enriching':
      case 'generating':
        return 'text-blue-600';
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-grey-600';
    }
  };

  const getStatusLabel = () => {
    if (!status) return 'Loading...';

    switch (status.status) {
      case 'uploaded':
        return 'Uploaded';
      case 'queued':
        return 'Queued for Processing';
      case 'extracting':
        return 'Extracting Metadata';
      case 'enriching':
        return 'Enriching with Market Data';
      case 'generating':
        return 'Generating Articles';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return status.status;
    }
  };

  if (error) {
    return (
      <div className="bg-surface rounded-lg shadow-sm border border-red-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-red-600">
            <XCircle className="w-5 h-5" />
            <h3 className="font-semibold text-lg">Error</h3>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-grey-400 hover:text-grey-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <p className="text-sm text-grey-600">{error}</p>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="bg-surface rounded-lg shadow-sm border border-grey-200 p-6">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
          <span className="text-sm text-grey-600">Loading status...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-grey-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <h3 className="font-semibold text-lg text-grey-900">
            {getStatusLabel()}
          </h3>
        </div>
        {onClose && status.status !== 'completed' && (
          <button
            onClick={onClose}
            className="text-grey-400 hover:text-grey-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-grey-700">
            Progress
          </span>
          <span className="text-sm font-medium text-grey-700">
            {status.progress_percentage}%
          </span>
        </div>
        <div className="w-full bg-grey-200 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ease-out ${
              status.status === 'failed' ? 'bg-red-600' :
              status.status === 'completed' ? 'bg-green-600' : 'bg-blue-600'
            }`}
            style={{ width: `${status.progress_percentage}%` }}
          />
        </div>
      </div>

      {/* Metadata - Only show when available */}
      {(status.company_code || status.company_name || status.announcement_title) && (
        <div className="space-y-3 mb-4 p-4 bg-grey-50 rounded-lg">
          {status.company_code && (
            <div className="flex items-start gap-2">
              <Building2 className="w-4 h-4 text-grey-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-grey-500">Company</p>
                <p className="text-sm font-medium text-grey-900 truncate">
                  {status.company_code}{status.company_name ? ` - ${status.company_name}` : ''}
                </p>
              </div>
            </div>
          )}

          {status.announcement_title && (
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-grey-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-grey-500">Announcement</p>
                <p className="text-sm font-medium text-grey-900 line-clamp-2">
                  {status.announcement_title}
                </p>
              </div>
            </div>
          )}

          {status.announcement_date && (
            <div className="flex items-start gap-2">
              <Calendar className="w-4 h-4 text-grey-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-grey-500">Date</p>
                <p className="text-sm font-medium text-grey-900">
                  {new Date(status.announcement_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {status.error_message && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm font-medium text-red-800 mb-1">
                Error Details
              </div>
              <div className="text-sm text-red-700">{status.error_message}</div>
            </div>
          </div>
        </div>
      )}

      {/* Articles Generated */}
      {status.status === 'completed' && status.article_count > 0 && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Newspaper className="w-5 h-5 text-green-600" />
            <div className="text-sm font-semibold text-green-900">
              {status.article_count} {status.article_count === 1 ? 'Article' : 'Articles'} Generated
            </div>
          </div>

          {/* Tabs - only show if both types exist */}
          {status.legacy_article_ids?.length > 0 && (
            <div className="flex border-b border-green-200 mb-3">
              <button
                onClick={() => setArticleTab('new')}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                  articleTab === 'new'
                    ? 'border-green-600 text-green-700'
                    : 'border-transparent text-green-500 hover:text-green-700'
                }`}
              >
                <span className="flex items-center gap-1">
                  <Newspaper className="w-3 h-3" />
                  New ({status.article_ids.length})
                </span>
              </button>
              <button
                onClick={() => setArticleTab('legacy')}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                  articleTab === 'legacy'
                    ? 'border-green-600 text-green-700'
                    : 'border-transparent text-green-500 hover:text-green-700'
                }`}
              >
                <span className="flex items-center gap-1">
                  <Archive className="w-3 h-3" />
                  Legacy ({status.legacy_article_ids.length})
                </span>
              </button>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {(articleTab === 'new' || !status.legacy_article_ids?.length
              ? status.article_ids
              : status.legacy_article_ids
            ).map((articleId) => (
              <Link
                key={articleId}
                to={`/articles/${articleId}`}
                className="inline-flex items-center px-3 py-1 bg-surface border border-green-300 text-green-700 text-sm font-medium rounded-md hover:bg-green-50 transition-colors"
              >
                View Article →
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        {status.status === 'completed' && (
          <>
            <Link
              to={`/announcements/${status.announcement_id}`}
              className="px-4 py-2 text-sm font-medium text-primary-100 bg-surface border border-primary-100 hover:bg-primary-50 rounded-lg transition-colors"
            >
              View Details
            </Link>
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-100 hover:bg-primary-700 rounded-lg transition-colors"
              >
                Close
              </button>
            )}
          </>
        )}
        {status.status === 'failed' && onClose && (
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}
